import { CheckIcon, ShieldCheckIcon, XIcon } from '@phosphor-icons/react'
import { TFunction } from 'i18next'
import z from 'zod'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { SortOrderSchema, TableColumn } from '@/components/ui/table'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { VendorModel, visitorStatusBadgeColorMap } from '@/models'
import { theme } from '@/theme/theme'

import { AccessTypeCell } from '../access-type-cell'
import {
  formatDateTime,
  formatPermissions,
  formatPermissionTimeRange,
} from '../visitors-dashboard.utils'
import * as S from './visitors-vendors.styled'

export const vendorsInitialFilters: VendorsFilters = {
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
  sortBy: undefined,
  sortOrder: undefined,
  status: 'all',
}

export const vendorsQueryFiltersSchema = z.object({
  limit: z.coerce.number().optional().default(DEFAULT_LIMIT),
  search: z.string().optional().default(''),
  skip: z.coerce.number().optional().default(0),
  sortBy: z.string().optional(),
  sortOrder: SortOrderSchema.optional(),
  status: z.enum(['all', 'active', 'expired', 'banned', 'suspended']).optional().default('all'),
})

export type VendorsFilters = z.infer<typeof vendorsQueryFiltersSchema>

export const getVendorColumns = ({
  t,
  timezone,
  customAccessName,
  onCheckIn,
}: {
  t: TFunction<['visitors']>
  timezone: string
  customAccessName?: string
  onCheckIn: (vendor: VendorModel) => void
}): TableColumn<VendorModel>[] => [
  {
    id: 'name',
    render: (item) => (
      <S.NameCell>
        <S.NameTextGroup>
          <Typography as="span" size="text-small" color="black800">
            {item.name}
          </Typography>
          {item.phone && (
            <Typography as="span" size="text-small" color="black800">
              {item.phone}
            </Typography>
          )}
        </S.NameTextGroup>
        {item.coi && (
          <Tooltip
            trigger={
              <S.IconBadge $bgColor={theme.colors.blue500}>
                <Icon icon={ShieldCheckIcon} color="white" size="1.2rem" />
              </S.IconBadge>
            }
          >
            {t('visitors:columns.coi')}
          </Tooltip>
        )}
      </S.NameCell>
    ),
    sortKey: 'name',
    title: t('visitors:dashboard.columns.vendorName'),
    width: '20rem',
  },
  {
    id: 'permissions',
    render: (item) => {
      const timeRange = formatPermissionTimeRange(item, timezone)
      return (
        <S.PermissionsCell>
          <S.PermissionRow>
            <S.RowText>
              <Typography as="span" size="text-small" color="black800">
                {formatPermissions(item, timezone)}
              </Typography>
            </S.RowText>
          </S.PermissionRow>
          {timeRange && (
            <Typography as="span" size="text-small" color="black800">
              {timeRange}
            </Typography>
          )}
        </S.PermissionsCell>
      )
    },
    sortAscLabel: t('visitors:columns.permissionsSortAsc'),
    sortDescLabel: t('visitors:columns.permissionsSortDesc'),
    sortKey: 'permission',
    title: t('visitors:columns.permissions'),
    width: '18rem',
  },
  {
    id: 'accessType',
    render: (item) => (
      <AccessTypeCell
        customAccess={item.customAccess}
        customAccessName={customAccessName}
        keyAccess={item.keyAccess}
        keylessAccess={item.keylessAccess}
        pinCode={item.pinCode}
      />
    ),
    title: t('visitors:dashboard.columns.accessType'),
    width: '16rem',
  },
  {
    id: 'currentAccess',
    render: (item) => (
      <Badge
        aria-label={
          item.permissionToEnter
            ? t('visitors:dashboard.status.active')
            : t('visitors:dashboard.status.inactive')
        }
        color={item.permissionToEnter ? 'green' : 'red'}
        size="small"
        variant="light"
      >
        <Icon
          icon={item.permissionToEnter ? CheckIcon : XIcon}
          color={item.permissionToEnter ? 'green500' : 'red500'}
          size="1.4rem"
        />
      </Badge>
    ),
    title: t('visitors:dashboard.columns.currentAccess'),
    width: '15rem',
  },
  {
    id: 'lastCheckIn',
    render: (item) => {
      if (!item.lastCheckIn) return EMPTY_VALUE
      const { date, time } = formatDateTime(item.lastCheckIn, timezone)
      return (
        <S.DateTimeCell>
          <Typography as="span" size="text-small" color="black800">
            {date}
          </Typography>
          <Typography as="span" size="text-small" color="black800">
            {time}
          </Typography>
        </S.DateTimeCell>
      )
    },
    sortAscLabel: t('visitors:dashboard.columns.lastCheckInSortAsc'),
    sortDescLabel: t('visitors:dashboard.columns.lastCheckInSortDesc'),
    sortKey: 'lastCheckIn',
    title: t('visitors:dashboard.columns.lastCheckIn'),
    width: '14rem',
  },
  {
    id: 'status',
    render: (item) => (
      <Badge color={visitorStatusBadgeColorMap[item.status]} size="small" variant="light">
        {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
      </Badge>
    ),
    title: t('visitors:columns.status'),
    width: '10rem',
  },
  {
    id: 'actions',
    render: (item) => {
      if (item.status !== 'active') return null
      return (
        <Button
          use="secondary"
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            onCheckIn(item)
          }}
        >
          {t('visitors:columns.checkIn')} <Icon icon={CheckIcon} size="1.4rem" />
        </Button>
      )
    },
    title: t('visitors:columns.actions'),
    width: '10rem',
  },
]
